import { Stack, YStack } from '@mythical/ui'
import { Video as VideoType } from 'app/blocks/video'
import React, { useEffect, useState } from 'react'

import VideoPlayer from '../primitive/VideoPlayer'
import ContentUnlockModal from './ContentUnlockModal'
import { observer } from 'mobx-react-lite'
import { useStores } from 'app/store'
import { UnlockButton } from './UnlockButton'

interface Props {
  contentId: string
  title?: string
  thumbnail?: string
  block: VideoType
  isUnlocked: boolean
  canUnlock: boolean
  planLevel: number | 'free'
  currentProgress?: number
  previewPercentage?: number
  onVideoPlay?: () => void
  onVideoPause?: () => void
  onVideoEnd?: () => void
  onVideoProgress?: (progress: number) => void
  onUnlock?: () => void
}

export const ContentVideoArea: React.FC<Props> = observer(
  ({ block, onUnlock, ...rest }) => {
    const [previewEnded, setPreviewEnded] = useState(false)
    const [isUnlocked, setIsUnlocked] = useState(rest.isUnlocked)
    const { userStore } = useStores()

    const onPreviewEnded = () => {
      setPreviewEnded(true)
    }

    useEffect(() => {
      setIsUnlocked(rest.isUnlocked)
      setPreviewEnded(false)
    }, [rest.isUnlocked])

    const data = JSON.parse(block.videoData)

    const handleUnlock = () => {
      setPreviewEnded(false)
      setIsUnlocked(true)
      onUnlock?.()
    }

    return (
      <YStack
        position="relative"
        minHeight={previewEnded ? 450 : 0}
        alignContent="center"
        justifyContent="center"
      >
        {previewEnded && (
          <ContentUnlockModal
            id={rest.contentId}
            planLevel={rest.planLevel}
            canUnlock={rest.canUnlock}
            onUnlock={() => handleUnlock()}
          />
        )}
        <Stack aspectRatio={16 / 9} w="100%">
          <VideoPlayer
            id={block.id}
            url={block.video}
            player={block.player}
            data={data}
            captions={block.closedCaptions}
            onVideoPreviewEnd={onPreviewEnded}
            currentProgress={rest.currentProgress}
            {...rest}
            isUnlocked={isUnlocked}
            userStore={userStore}
            contentId={rest.contentId}
          />
          {!isUnlocked && rest.canUnlock && (
            <UnlockButton
              id={rest.contentId}
              onUnlock={() => handleUnlock()}
              variant="secondary"
              buttonProps={{
                pos: 'absolute',
                top: '$2',
                left: '$2',
                zIndex: 1,
                size: '$2',
                $gtXs: { size: '$3', top: '$3', left: '$3' },
              }}
            />
          )}
        </Stack>
      </YStack>
    )
  }
)

ContentVideoArea.displayName = 'ContentVideoArea'
